import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Search, FileText, TrendingUp, Heart, Car, Users, Home, BookOpen, ArrowRight, Info } from "lucide-react";
import ArticleCard from "../components/ArticleCard";
import SEOHead from "../components/SEOHead";
import AdSensePlaceholder from "../components/AdSensePlaceholder";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  administratif: FileText,
  finance: TrendingUp,
  sante: Heart,
  auto: Car,
  famille: Users,
  "vie-pratique": Home,
};

const GSC_PRIORITY_TOPICS = [
  "acte de naissance en ligne", "CNIE", "certificat de résidence", "AMO", "SMIG 2026", "vignette auto"
];

const GSC_PRIORITY_ARTICLES = [
  { slug: "carte-identite-nationale-cnie-maroc", title: "CNIE au Maroc" },
  { slug: "demander-acte-naissance-watiqa-maroc", title: "Acte de naissance en ligne" },
  { slug: "certificat-residence-maroc", title: "Certificat de résidence" },
  { slug: "amo-maroc", title: "AMO au Maroc" },
  { slug: "mariage-maroc-documents", title: "Documents mariage" },
  { slug: "smig-maroc-2026", title: "SMIG Maroc 2026" },
  { slug: "vignette-auto-maroc-2026", title: "Vignette auto 2026" },
  { slug: "assurance-auto-maroc-2026", title: "Assurance auto 2026" },
];

const CATEGORY_COLORS = {
  administratif: "text-blue-600 bg-blue-50 border-blue-200",
  finance: "text-emerald-600 bg-emerald-50 border-emerald-200",
  sante: "text-rose-600 bg-rose-50 border-rose-200",
  auto: "text-amber-600 bg-amber-50 border-amber-200",
  famille: "text-purple-600 bg-purple-50 border-purple-200",
  "vie-pratique": "text-slate-600 bg-slate-50 border-slate-200",
};

export default function HomePage() {
  const location = useLocation();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [pillars, setPillars] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Promise.allSettled : si un endpoint échoue, les autres continuent
    Promise.allSettled([
      axios.get(`${API}/articles`),
      axios.get(`${API}/categories`),
      axios.get(`${API}/pillars`),
    ]).then(([artResult, catResult, pilResult]) => {
      if (artResult.status === "fulfilled") {
        const art = artResult.value.data;
        setArticles(Array.isArray(art) ? art : (art?.articles || []));
      }
      if (catResult.status === "fulfilled") {
        const cat = catResult.value.data;
        setCategories(Array.isArray(cat) ? cat : (cat?.categories || []));
      }
      if (pilResult.status === "fulfilled") {
        const pil = pilResult.value.data;
        setPillars(Array.isArray(pil) ? pil : (pil?.pillars || []));
      }
    }).finally(() => setLoading(false));
  }, []);

  const filteredArticles = search.trim()
    ? articles.filter((a) =>
        a.title.toLowerCase().includes(search.toLowerCase()) ||
        (a.excerpt || "").toLowerCase().includes(search.toLowerCase()) ||
        (a.mainKeyword || "").toLowerCase().includes(search.toLowerCase())
      )
    : articles;

  const displayedArticles = search.trim() ? filteredArticles : articles;

  const hasQueryString = Boolean(location.search);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "MarocPratique",
    url: "https://marocpratique.com",
    description: "Le guide pratique des Marocains : démarches administratives, prix, délais.",
  };

  return (
    <>
      <SEOHead
        title="MarocPratique — Guides démarches, papiers, finance et vie au Maroc"
        description="Guides pratiques pour comprendre vos démarches au Maroc : CIN, passeport, état civil, banque, assurance, salaire, santé, auto et vie quotidienne."
        canonical="https://marocpratique.com/"
        jsonLd={jsonLd}
        type="website"
        noIndex={hasQueryString}
      />

      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white py-14 sm:py-20"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-blue-200 text-sm font-semibold uppercase tracking-widest mb-3">
            Guide Pratique du Maroc
          </p>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-2">
            Guides pratiques pour vos démarches au Maroc
          </h1>
          <p className="text-blue-100 text-base sm:text-lg mb-8 max-w-xl mx-auto">
            Que cherchez-vous ? Démarches administratives, prix, délais et conseils concrets.
          </p>

          {/* Search */}
          <div className="relative max-w-2xl mx-auto mb-6">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Ex : passeport, assurance auto, SMIG..."
              data-testid="home-search-input"
              className="w-full pl-12 pr-4 py-4 text-slate-900 bg-white rounded-2xl border-0 shadow-xl text-base focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>

          {/* Popular topics */}
          <div className="flex flex-wrap justify-center gap-2">
            {GSC_PRIORITY_TOPICS.map((topic) => (
              <button
                key={topic}
                onClick={() => setSearch(topic)}
                data-testid={`popular-topic-${topic.replace(/\s+/g, "-")}`}
                className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-sm rounded-full border border-white/20 transition-colors"
              >
                {topic}
              </button>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Disclaimer — site indépendant non officiel */}
        <div className="pt-5 pb-1">
          <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
            <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-xs text-slate-600 leading-relaxed">
              <strong className="text-slate-700">Site indépendant et non officiel.</strong>{" "}
              MarocPratique n'est pas affilié à une administration ou organisme public marocain.
              Les informations sont publiées à titre indicatif, à partir d'un travail de recherche et de simplification.
              Elles peuvent évoluer — vérifiez toujours auprès des sites officiels ou administrations compétentes
              avant d'effectuer une démarche.{" "}
              <Link to="/mentions-legales" className="text-blue-600 hover:underline font-medium">
                En savoir plus
              </Link>
            </p>
          </div>
        </div>

        {/* Categories Grid */}
        {!search && (
          <section data-testid="categories-section" className="py-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">
                Catégories populaires
              </h2>
            </div>
            {loading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-24 bg-slate-200 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {categories.map((cat) => {
                  const Icon = CATEGORY_ICONS[cat.slug] || FileText;
                  const colorClass = CATEGORY_COLORS[cat.slug] || "text-slate-600 bg-slate-50 border-slate-200";
                  return (
                    <Link
                      key={cat.slug}
                      to={`/categorie/${cat.slug}`}
                      data-testid={`category-card-${cat.slug}`}
                      className={`flex flex-col items-center justify-center gap-2 p-4 rounded-xl border transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 ${colorClass}`}
                    >
                      <Icon className="w-6 h-6" />
                      <span className="font-heading font-semibold text-sm text-center leading-tight">
                        {cat.label}
                      </span>
                      {cat.article_count > 0 && (
                        <span className="text-xs opacity-60">
                          {cat.article_count} {cat.article_count === 1 ? "article" : "articles"}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* Démarches essentielles — maillage interne vers articles prioritaires */}
        {!search && (
          <section className="py-8 border-t border-slate-100">
            <h2 className="font-heading text-xl sm:text-2xl font-bold text-slate-900 mb-5">
              Démarches les plus recherchées
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {GSC_PRIORITY_ARTICLES.map((item) => (
                <Link
                  key={item.slug}
                  to={`/article/${item.slug}`}
                  className="flex items-center gap-2 px-4 py-3 bg-slate-50 hover:bg-blue-50 border border-slate-200 hover:border-blue-200 rounded-xl text-sm font-medium text-slate-700 hover:text-blue-700 transition-all duration-200 group"
                >
                  <ArrowRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-blue-500 flex-shrink-0" />
                  <span className="line-clamp-1">{item.title}</span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* AdSense top */}
        <AdSensePlaceholder position="banner" />

        {/* Articles Section */}
        <section data-testid="articles-section" className="py-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">
              {search ? `Résultats pour "${search}"` : "Derniers guides pratiques"}
            </h2>
            {search && filteredArticles.length > 0 && (
              <span className="text-sm text-slate-500">{filteredArticles.length} résultat(s)</span>
            )}
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-72 bg-slate-200 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : search.trim() && displayedArticles.length === 0 ? (
            <div
              data-testid="no-results"
              className="text-center py-16 text-slate-500"
            >
              <Search className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-lg font-medium">Aucun article trouvé pour "{search}"</p>
              <p className="text-sm mt-2">Essayez un autre mot-clé</p>
              <button
                onClick={() => setSearch("")}
                className="mt-4 text-blue-600 hover:underline text-sm"
              >
                Voir tous les articles
              </button>
            </div>
          ) : Array.isArray(displayedArticles) && displayedArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {displayedArticles.slice(0, 6).map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          ) : null}
        </section>

        {/* Pillar Guides section */}
        {!search && pillars.length > 0 && (
          <section data-testid="pillar-guides-section" className="py-8 border-t border-slate-100">
            <div className="flex items-center gap-3 mb-6">
              <BookOpen className="w-5 h-5 text-blue-600" />
              <h2 className="font-heading text-xl sm:text-2xl font-bold text-slate-900">
                Guides complets par thème
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {pillars.map((pillar) => (
                <Link
                  key={pillar.slug}
                  to={`/pilier/${pillar.slug}`}
                  data-testid={`pillar-card-${pillar.slug}`}
                  className="flex items-start gap-3 p-4 bg-white border border-slate-200 rounded-xl hover:border-blue-200 hover:shadow-md transition-all duration-200 group"
                >
                  <div className="w-9 h-9 bg-blue-600 text-white rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <BookOpen className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-heading font-semibold text-slate-800 text-sm leading-snug group-hover:text-blue-700 transition-colors line-clamp-2">
                      {pillar.title}
                    </p>
                    {pillar.seoDescription && (
                      <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                        {pillar.seoDescription}
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
}
